import React, { useState, useEffect } from 'react';
import randomString from 'random-string';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { RingingDialog, HungUpDialog } from '../index';

import { joinRoom } from '../../services';
import { GET_SESSION } from '../../services/patient';

import { alertError } from '../../redux/stateActions';

import { userRole } from '../../constant';

import socket from '../../socket';

import * as cookiesManager from '../../utils/cookiesManager';

const ringtone = '/resources/audios/patientRingtone.mp3';

const ReceivingCall = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const user = useSelector((state) => state.user);

    /*--------------- Call Control Controller ---------------*/
    const [micOn, setMicOn] = useState(true);
    const [cameraOn, setCameraOn] = useState(true);

    useEffect(() => {
        setMicOn(true);
        setCameraOn(true);
        cookiesManager.setDevices({ micEnabled: true, webcamEnabled: true });
    }, []);

    const handleMic = () => {
        const devicesCookie = cookiesManager.getDevices();
        setMicOn(!micOn);
        cookiesManager.setDevices({ ...devicesCookie, micEnabled: !micOn });
    };

    const handleCamera = () => {
        const devicesCookie = cookiesManager.getDevices();
        setCameraOn(!cameraOn);
        cookiesManager.setDevices({ ...devicesCookie, webcamEnabled: !cameraOn });
    };

    const [open, setOpen] = useState(false);
    const [sessionData, setSessionData] = useState({});
    const [sessionId, setSessionId] = useState(null);

    const [audio] = useState(new Audio(ringtone));

    // Get session
    const { data, error } = useQuery(GET_SESSION, {
        variables: { id: sessionId },
        skip: !sessionId || _.isEmpty(user),
    });

    const resetData = () => {
        setOpen(false);
        setSessionId(null);
        setSessionData({});
    };

    useEffect(() => {
        if (error) {
            dispatch(alertError(`Cannot get Provider: ${error.message}`));
        }

        if (data && !_.isEmpty(data.session)) {
            setSessionData(data.session);
            setOpen(true);

            socket.emit('patientReceivedCall', {
                staffId: data.session.meet.staff.id,
                patientId: user.patient.id,
                sessionId: data.session.id,
            });
        }
    }, [error, data]);

    const handleJoinRoom = async (data) => {
        try {
            const params = new URLSearchParams();
            params.append('roomId', data.roomId);
            params.append('sessionId', randomString({ length: 16 }).toLowerCase());
            params.append('role', userRole.PATIENT);
            params.append(
                'displayName',
                `${user.fullname}|${user.patient?.id}||${user.avatar?.url}|Patient`
            );
            params.append('password', '');

            const joinRes = await joinRoom(data.roomId, params);

            if (joinRes.data) {
                history.push({
                    pathname: `/call/${data.sessionId}`,
                    search: `?roomId=${data.roomId}&session=${joinRes.data.session}`,
                });
                resetData();
            } else {
                throw new Error(joinRes);
            }
        } catch (error) {
            console.log('received call', error);
            dispatch(alertError(`Cannot join received call session: ${error.message}`));
        }
    };

    const handleRejectCall = () => {
        socket.emit('patientRejectCall', {
            staffId: sessionData.meet.staff.id,
            patientId: user.patient.id,
            sessionId: sessionData.id,
        });

        resetData();
    };

    const handleAnswerCall = () => {
        socket.timeout(3000).emit(
            'patientAnswerCall',
            {
                staffId: sessionData.meet.staff.id,
                patientId: user.patient.id,
                sessionId: sessionData.id,
            },
            (err, response) => {
                if (err) {
                    resetData();
                }
                if (response) {
                    if (_.isEmpty(response) || response === 'error') {
                        resetData();
                    }
                }
            }
        );
    };

    useEffect(() => {
        // if socket is disconnected while ringing dialog is open
        socket.on('disconnect', () => {
            resetData();
        });

        socket.on('providerSetUpRoom', (data) => {
            handleJoinRoom(data);
        });

        socket.on('providerCancelCall', () => {
            setOpenHungUpDialog('CANCEL');
            resetData();
        });

        socket.on('providerSkipQueue', () => {
            setOpenHungUpDialog('SKIP');
            resetData();
        });

        socket.on('checkPaitentOnline', (data, callback) => {
            callback('ok');
        });
    }, []);

    useEffect(() => {
        const getPath = location.pathname.split('/');

        // clear old event listener
        socket.off('providerCalling');

        socket.on('providerCalling', async (data) => {
            const payload = {
                sessionId: data.sessionId,
                staffId: data.staffId,
                patientId: user.patient.id,
            };

            // ringing window already open
            if (open) {
                socket.emit('patientInAnotherCall', payload);
                return;
            }

            // patient in group or open call
            if (['groupcall', 'opencall'].includes(getPath[1])) {
                socket.emit('patientInAnotherCall', payload);
                return;
            }

            // patient in one on one call
            if (getPath[1] === 'call') {
                if (getPath[2] === data.sessionId) {
                    const { search } = location;
                    const queryParams = new URLSearchParams(search);
                    const roomId = queryParams.get('roomId');

                    socket.emit('patientAlreadyInRoom', { ...payload, roomId });
                } else {
                    socket.emit('patientInAnotherCall', payload);
                }
                return;
            } else {
                setSessionId(data.sessionId);
            }
        });
    }, [location, open]);

    useEffect(() => {
        open ? audio.play() : audio.pause();
        audio.volume = 0.5;
        audio.loop = true;
    }, [open]);

    useEffect(() => {
        let pingInterval;
        let count = 0;

        if (open) {
            socket.on('pingTarget', (data, callback) => {
                callback('ok');
            });

            if (!_.isEmpty(sessionData)) {
                pingInterval = setInterval(() => {
                    if (count >= 5) {
                        handleCloseHungUpDialog();
                        resetData();
                        clearInterval(pingInterval);
                        pingInterval = 0;
                        count = 0;
                    }

                    socket
                        .timeout(3000)
                        .emit('pingTarget', { id: sessionData.meet.staff.id }, (err, response) => {
                            if (err) {
                                count++;
                            }
                            if (response) {
                                if (_.isEmpty(response) || response === 'error') {
                                    count++;
                                    return;
                                }
                                if (response[0] === 'ok') {
                                    count = 0;
                                }
                            }
                        });
                }, 1000);
            }
        }

        if (!open) {
            clearInterval(pingInterval);
            pingInterval = 0;
            count = 0;
        }
    }, [open, sessionData]);

    /*---------- Dialog ----------*/
    const [openHungUpDialog, setOpenHungUpDialog] = useState('');

    const handleCloseHungUpDialog = () => {
        setOpenHungUpDialog('');
    };

    return (
        <div>
            <HungUpDialog
                open={Boolean(openHungUpDialog)}
                onClose={handleCloseHungUpDialog}
                type={openHungUpDialog}
            />
            <RingingDialog
                open={open}
                data={sessionData?.meet?.staff}
                onClose={handleRejectCall}
                onAnswer={handleAnswerCall}
                $desktopLayout={mdUp}
                micOn={micOn}
                handleMic={handleMic}
                cameraOn={cameraOn}
                handleCamera={handleCamera}
            />
        </div>
    );
};

export default ReceivingCall;
